
// WHY: iOS TMA: WKWebView NSURLCache drops multi-MB bodies and no Service Worker — without IndexedDB every session
// re-downloads
import { browserNavigator } from './browserSurface';
import { numberField, rawField } from './jsonBody';

const DB_NAME = 'shot-assets';
const STORE = 'blobs';
const DB_VERSION = 1;
const MIN_FREE_BYTES = 80 * 1024 * 1024;

interface StoredAsset {
  blob: Blob;
  storedAt: number;
  bytes: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') { resolve(null); return; }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch { resolve(null); }
  });
  return dbPromise;
}

function readStoredAsset(value: unknown): StoredAsset | null {
  const blob = rawField(value, 'blob');
  const storedAt = numberField(value, 'storedAt');
  const bytes = numberField(value, 'bytes');
  if (!(blob instanceof Blob) || storedAt === undefined || bytes === undefined) return null;
  return { blob, storedAt, bytes };
}

function idbGet(db: IDBDatabase, url: string): Promise<StoredAsset | null> {
  return new Promise((resolve) => {
    try {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(url);
      req.onsuccess = () => resolve(readStoredAsset(req.result));
      req.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}

function idbPut(db: IDBDatabase, url: string, value: StoredAsset): Promise<void> {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(value, url);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch { resolve(); }
  });
}

let persistTried = false;
function ensurePersist(): void {
  if (persistTried || typeof navigator === 'undefined') return;
  persistTried = true;
  try { void browserNavigator().storage?.persist?.().catch(() => undefined); } catch { }
}

async function hasRoom(bytes: number): Promise<boolean> {
  try {
    const est = await browserNavigator().storage?.estimate?.();
    if (!est) return true;
    const quota = est.quota ?? 0;
    if (quota === 0) return true;
    const free = quota - (est.usage ?? 0);
    return free - bytes >= MIN_FREE_BYTES;
  } catch { return true; }
}

async function fetchBlob(url: string, onChunk?: (n: number) => void): Promise<Blob> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`asset fetch ${res.status}: ${url}`);
  const reader = res.body?.getReader();
  if (!reader) {
    const blob = await res.blob();
    onChunk?.(blob.size);
    return blob;
  }
  const type = res.headers.get('content-type') ?? '';
  const chunks: BlobPart[] = [];
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    onChunk?.(value.length);
  }
  return new Blob(chunks, type ? { type } : undefined);
}

export async function getAsset(url: string, onChunk?: (n: number) => void): Promise<Blob> {
  const db = await openDb();
  if (db) {
    const hit = await idbGet(db, url);
    if (hit?.blob) { onChunk?.(hit.bytes || hit.blob.size); return hit.blob; }
  }
  const blob = await fetchBlob(url, onChunk);
  if (db) {
    ensurePersist();
    void hasRoom(blob.size).then((room) => {
      if (room) void idbPut(db, url, { blob, storedAt: Date.now(), bytes: blob.size });
    });
  }
  return blob;
}

export async function getAssetImage(url: string, onChunk?: (n: number) => void): Promise<HTMLImageElement> {
  const blob = await getAsset(url, onChunk);
  const objUrl = URL.createObjectURL(blob);
  const img = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`asset decode failed: ${url}`));
      img.src = objUrl;
    });
    await img.decode().catch(() => undefined);
  } finally {
    URL.revokeObjectURL(objUrl);
  }
  return img;
}

export async function pruneAssets(keepUrls: Iterable<string>): Promise<void> {
  const db = await openDb();
  if (!db) return;
  const keep = new Set(keepUrls);
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      const req = tx.objectStore(STORE).openCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) return;
        if (typeof cur.key === 'string' && !keep.has(cur.key)) cur.delete();
        cur.continue();
      };
      req.onerror = () => resolve();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch { resolve(); }
  });
}
